import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  FaFileDownload,
  FaCcVisa,
  FaCcMastercard,
  FaCreditCard,
} from "react-icons/fa";
import "./PaymentHistory.scss";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PaymentFilter from "./PaymentFilter";
import PaymentHistoryFilter from "./PaymentHistoryFilter";
import { useNavigate } from "react-router-dom";
import { SubscriptionApi } from "../../../utils/api/SubscriptionApi";
import CloseIcon from "../../../components/icons/CloseIcon";
import TableHeaderRow from "../../../components/tables/TableHeaderRow";
import TableHeaderCell from "../../../components/tables/TableHeaderCell";
import Loading from "../../loading/Loading";
import TableDataRow from "../../../components/tables/TableDataRow";
import TableDataCell from "../../../components/tables/TableDataCell";
import FileDownloadIcon from "../../../components/icons/FileDownloadIcon";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";

const PaymentHistory = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const navigate = useNavigate();

  const [activeFilters, setActiveFilters] = useState({
    start_date: "",
    end_date: "",
  });

  const [pagination, setPagination] = useState<{
    has_more: boolean;
    next_page: string | null;
  }>({
    has_more: false,
    next_page: null,
  });

  const PAGE_LIMIT = 10;

  const getHistory = async (reset = false) => {
    setIsLoading(true);

    try {
      const cursor = reset ? undefined : pagination.next_page;

      const res = await SubscriptionApi.getStripePaymentHistory({
        limit: PAGE_LIMIT,
        starting_after: cursor || undefined,
        from_date: activeFilters.start_date || undefined,
        to_date: activeFilters.end_date || undefined,
      });

      if (res.data?.success) {
        const data = res.data.data;

        setHistoryData(data);

        setPagination({
          has_more: res.data.pagination.has_more,
          next_page: res.data.pagination.next_page,
        });
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPagination({ has_more: false, next_page: null });
    getHistory(true);
  }, [activeFilters]);

  const handleApplyFilters = (values: any) => {
    setActiveFilters({
      start_date: values.start_date,
      end_date: values.end_date,
    });
  };

  const handleClearAll = () => {
    setActiveFilters({
      start_date: "",
      end_date: "",
    });
  };

  const formatAmountWithCurrency = (
    amount: number | null | undefined,
    currency: string,
  ) => {
    if (amount === null || amount === undefined) return `${currency} 0`;

    const val = amount / 100;

    const formattedNum = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

    return `${currency?.toUpperCase()} ${formattedNum}`;
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return (
      <div className="date-cell-content">
        <p className="date-text font-inter-400">
          {date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        <p className="time-text font-inter-400">
          {date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    );
  };

  const getCardBrand = (item: any): string => {
    return (
      item?.charge?.payment_method_details?.card?.brand ||
      item?.payment_intent?.payment_method_details?.card?.brand ||
      ""
    );
  };

  const getCardIcon = (brand: string) => {
    const b = brand?.toLowerCase();

    if (b === "visa")
      return (
        <img src="/images/cards/visa.svg" alt="Visa" className="card-icon" />
      );

    if (b === "mastercard")
      return (
        <img
          src="/images/cards/mastercard.svg"
          alt="Mastercard"
          className="card-icon"
        />
      );

    if (b === "amex")
      return (
        <img src="/images/cards/amex.svg" alt="Amex" className="card-icon" />
      );

    return (
      <img
        src="/images/cards/credit-card.svg"
        alt="Credit Card"
        className="card-icon"
      />
    );
  };

  const cleanDescription = (text: string) => {
    if (!text) return "Subscription Charge";

    let clean = text.replace(/^\d+\s[x×]\s/, "");

    if (clean.includes(" (at")) {
      clean = clean.split(" (at")[0];
    }

    return clean;
  };

  const getInvoiceMetadata = (item: any) => {
    return item.invoice?.metadata || item.metadata || {};
  };

  const getSubscriptionName = (item: any) => {
    const meta = getInvoiceMetadata(item);

    const propertyId = meta.property_id;
    const propertyAddress = meta.property_address;
    const listingType = meta.listing_type;

    if (propertyId && propertyAddress) {
      if (listingType === "sale") {
        return "Property Marketing Fee One-off (For Sale)";
      }

      if (listingType === "rent") {
        return "Property Marketing Fee One-off (For Rent)";
      }

      return "Property Marketing Fee One-off";
    }

    return "Agent Marketing Subscription";
  };

  const getToday = () => {
    return new Date().toISOString().split("T")[0];
  };

  const handleInvoiceDownload = async (invoicePath: string) => {
    const fileName = invoicePath.split("/").pop();
    if (!fileName) return;

    const res = await SubscriptionApi.downloadCustomInvoice(fileName);

    if (!res.success) {
      alert("Failed to download invoice");
      return;
    }

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getPointsInfo = (item: any) => {
    const meta = item.metadata || {};

    if (meta.is_paid_with_points === "true") {
      return {
        used: true,
        points: Number(meta.redeemed_points || 0),
      };
    }

    return { used: false, points: 0 };
  };

  const getGSTAmount = (item: any) => {
    const gstLine = item.lines?.data?.find((l: any) =>
      l.description?.toLowerCase().includes("gst"),
    );

    if (gstLine?.amount) {
      return gstLine.amount;
    }

    if (
      Array.isArray(item.total_tax_amounts) &&
      item.total_tax_amounts.length > 0
    ) {
      return item.total_tax_amounts.reduce(
        (sum: number, t: any) => sum + (t.amount || 0),
        0,
      );
    }

    if (item.tax) {
      return item.tax;
    }

    return 0;
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="payment-history-container">
          <div className="agent-payment-header-section">
            <h3>Transaction history</h3>

            <div className="payment-sub-header">
              <p className="payment-sub-title font-inter-400">
                View your payment history, download invoices and more
              </p>

              <PaymentFilter />
            </div>
          </div>

          {(activeFilters.start_date || activeFilters.end_date) && (
            <div className="applied-filters-section">
              <p className="applied-label font-inter-400">Applied filters</p>
              <div className="chips-container">
                {activeFilters.start_date && (
                  <div className="filter-chip font-inter-400">
                    <span>
                      {dayjs(activeFilters.start_date).format("DD/MM/YYYY")}
                      {activeFilters.end_date
                        ? ` - ${dayjs(activeFilters.end_date).format("DD/MM/YYYY")}`
                        : ""}
                    </span>
                    <span
                      className="close-chip font-inter-400"
                      onClick={handleClearAll}
                    >
                      <CloseIcon />
                    </span>
                  </div>
                )}
                <button
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearAll}
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}

          <div className="payment-table-container">
            <table>
              <TableHeaderRow className="table-header-row">
                <TableHeaderCell minWidth={180}>PAYMENT FOR</TableHeaderCell>
                <TableHeaderCell minWidth={150}>TOTAL</TableHeaderCell>
                <TableHeaderCell minWidth={200}>DATE</TableHeaderCell>
                <TableHeaderCell minWidth={180}>PAYMENT METHOD</TableHeaderCell>
                <TableHeaderCell minWidth={120}>STATUS</TableHeaderCell>
                <TableHeaderCell minWidth={150}>ACTION</TableHeaderCell>
              </TableHeaderRow>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div
                        className="list-page-loading"
                        style={{
                          height: "300px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Loading />
                      </div>
                    </td>
                  </tr>
                ) : historyData.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div
                        className="data-not-found"
                        style={{
                          height: "200px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <p>No payment history found.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  historyData.map((item: any) => (
                    <TableDataRow className="contain-row" key={item.id}>
                      <TableDataCell minWidth={180}>
                        <div className="payment-desc-cell">
                          <span className="main-title font-inter-400">
                            {getSubscriptionName(item)}
                          </span>
                          {(() => {
                            const meta = getInvoiceMetadata(item);

                            if (!meta.property_address || !meta.property_id)
                              return null;

                            return (
                              <span
                                className="sub-info-link font-inter-400"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  navigate(
                                    `/properties/view/${meta.property_id}`,
                                  )
                                }
                              >
                                {meta.property_address}
                              </span>
                            );
                          })()}
                        </div>
                      </TableDataCell>

                      <TableDataCell minWidth={150}>
                        {(() => {
                          const isPointsUsed =
                            item.metadata?.is_paid_with_points === "true";
                          const redeemedPoints = Number(
                            item.metadata?.redeemed_points || 0,
                          );
                          const gstAmount = getGSTAmount(item);

                          return (
                            <div className="amount-info-cell amount-info-cell--stacked">
                              {isPointsUsed && redeemedPoints > 0 && (
                                <span className="points-used-text font-inter-400">
                                  {redeemedPoints} Points +
                                </span>
                              )}
                              <p className="amount-text font-inter-400">
                                {formatAmountWithCurrency(
                                  item.total,
                                  item.currency,
                                )}
                              </p>
                              {gstAmount > 0 && (
                                <span className="tax-label font-inter-400">
                                  incl. {item.currency?.toUpperCase()}{" "}
                                  {gstAmount / 100} GST
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </TableDataCell>

                      <TableDataCell minWidth={200}>
                        {formatDate(item.created)}
                      </TableDataCell>

                      <TableDataCell minWidth={180}>
                        {(() => {
                          const total = item.total;
                          const discount = item.discount;
                          const subMeta =
                            item.subscription_details?.metadata || {};
                          const itemMeta = item.metadata || {};
                          const firstLineDesc =
                            item.lines?.data?.[0]?.description || "";

                          const isFreeCoupon =
                            total === 0 &&
                            discount &&
                            discount.coupon?.percent_off === 100;

                          const isTrialMetadata =
                            total === 0 &&
                            (subMeta.is_free_trial === "true" ||
                              itemMeta.is_free_trial === "true");

                          const isTrialDescription =
                            total === 0 &&
                            firstLineDesc
                              .toLowerCase()
                              .includes("trial period");
                          if (
                            isFreeCoupon ||
                            isTrialMetadata ||
                            isTrialDescription
                          ) {
                            let label = "Free Trial";

                            return (
                              <div className="method-cell font-inter-400">
                                <span className="agency-text">{label}</span>
                              </div>
                            );
                          }

                          const isPointsUsed =
                            item.metadata?.is_paid_with_points === "true";
                          const redeemedPoints = Number(
                            item.metadata?.redeemed_points || 0,
                          );

                          return (
                            <div className="method-cell font-inter-400 method-cell--stacked">
                              {item.metadata?.payment_method_source ===
                                "agency" && (
                                <div className="agency-text font-inter-400">
                                  Agency payment method
                                </div>
                              )}

                              {isPointsUsed && redeemedPoints > 0 && (
                                <div className="points-text">
                                  Points redemption +
                                </div>
                              )}

                              <div
                                className="card-row"
                                style={{ marginTop: "4px" }}
                              >
                                {getCardIcon(getCardBrand(item))}
                                <span>
                                  ending in{" "}
                                  {item.charge?.payment_method_details?.card
                                    ?.last4 || "****"}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </TableDataCell>

                      <TableDataCell minWidth={120}>
                        <span className={`status-badge ${item.status}`}>
                          {item.status}
                        </span>
                      </TableDataCell>

                      <TableDataCell minWidth={150}>
                        {item.metadata?.custom_pdf_link ? (
                          <button
                            onClick={() =>
                              handleInvoiceDownload(
                                item.metadata.custom_pdf_link,
                              )
                            }
                            className="invoice-btn font-inter-500"
                            style={{ cursor: "pointer", border: "none" }}
                          >
                            <FileDownloadIcon /> Invoice
                          </button>
                        ) : item.invoice_pdf ? (
                          <a
                            href={item.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            className="invoice-btn font-inter-500"
                          >
                            <FileDownloadIcon /> Stripe Invoice
                          </a>
                        ) : (
                          <span className="no-invoice">Processing...</span>
                        )}
                      </TableDataCell>
                    </TableDataRow>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && historyData.length > 0 && (
            <div className="pagination-footer">
              <p className="pagination-info"></p>
              <div className="pagination-actions">
                <button onClick={() => getHistory(true)}>Go to 1st page</button>
                <button
                  disabled={!pagination.has_more}
                  onClick={() => getHistory(false)}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <PaymentHistoryFilter
            onApply={handleApplyFilters}
            onClear={handleClearAll}
            currentFilters={activeFilters}
          />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default PaymentHistory;
